// Bilingual content (EN + DE) for Ordo Rex Studio.
// Access via useT() from ../i18n/LanguageContext.

export const content = {
  en: {
    // ── Common / nav / footer ────────────────────────────────
    nav: {
      home: "Home",
      method: "Method",
      services: "Services",
      studio: "Studio",
      faq: "FAQ",
      enquiry: "Enquiry",
      enquire: "Enquire",
    },
    common: {
      begin_enquiry: "Begin a private enquiry",
      the_method: "The Method",
      view_all: "View all eight services",
      read_full_method: "Read the full method",
      meet_studio: "Meet the founder",
      scroll: "Scroll",
      volumeOf: (n, label) => `Volume ${n} · ${label}`,
    },
    footer: {
      quiet_order_1: "Order,",
      quiet_order_2: "built to last.",
      begin_link: "Begin a private enquiry →",
      studio: "Studio",
      studio_lines: ["Vienna, Austria", "By private appointment"],
      studio_note: "Available across Europe.\nNo remote consultations.",
      studio_langs: "Sessions in English · Italiano · Español",
      navigate: "Navigate",
      impressum: "Impressum",
      copyright: (y) => `© ${y} Ordo Rex Studio. All rights reserved.`,
    },

    // ── Home ─────────────────────────────────────────────────
    hero: {
      eyebrow_1: "Est. Vienna",
      eyebrow_2: "Private Wardrobe Strategy",
      studio_suffix: "STUDIO",
      description:
        "Private wardrobe strategy for individuals seeking clarity, structure and refined personal style — conducted in person, by the same hand, at your home or alongside you.",
    },
    videoHero: {
      eyebrow: "A story of quiet order",
      title: "ORDO REX STUDIO",
      subtitle: "Heritage of order — privately built.",
      scroll: "Scroll to enter",
      mute: "Sound",
      unmute: "Sound",
    },
    quote84: {
      eyebrow: "",
      pre: "Eighty-four percent of people feel they have",
      italic: "nothing to wear.",
      post: "Their wardrobe disagrees.",
      credit: "— Vestiaire Collective × WRÅD, 2024",
    },
    essence: {
      number: "I",
      eyebrow: "The System",
      italic: "A wardrobe that finally makes sense.",
      heading: "Ordo Rex Studio is the system behind an effortless wardrobe.",
      body:
        "Whether that means editing what you own, defining your style direction, preparing for a specific occasion or navigating a life transition, every session is conducted in person, by the same hand, at your home, alongside you.",
      meta_1: "Vienna and across Europe",
      meta_2: "English · Italian · Spanish",
      meta_3: "No remote consultations",
    },
    pillars: {
      eyebrow: "II — The Method",
      title_1: "You own more outfits than you own clothes.",
      title_2: "",
      items: [
        {
          key: "declutter",
          title: "Edit",
          body: "Everything comes out. Every piece receives a decision: keep, tailor, sell, donate, archive, replace. What stays is catalogued, so you know exactly what you own.",
          link: "the-edit",
        },
        {
          key: "style",
          title: "Style",
          body: "Build outfits from what you own, define your palette, and discover your signature look.",
          link: "the-system",
        },
        {
          key: "maintain",
          title: "Maintain",
          body: "Seasonal edits, shopping guidance, and ongoing support to keep your wardrobe effortless.",
          link: "the-refresh",
        },
      ],
      learn_more: "Learn more →",
    },
    instagram: {
      eyebrow: "Follow the studio",
      title_1: "Order, built to last",
      title_2: "",
      handle: "@ordorexstudio",
      cta: "Follow on Instagram →",
    },
    manifestoPreview: {
      eyebrow: "Manifesto · The Method",
      title_1: "A full wardrobe",
      title_2: "and nothing to wear",
      title_3: "is not a fashion problem.",
      subtitle: "It is a system problem.",
    },
    servicesTeaser: {
      eyebrow: "Eight ways · One system",
      title_1: "Eight ways",
      title_2: "to work together.",
    },
    studioTeaser: {
      eyebrow: "III — The Studio",
      title_1: "The person",
      title_2: "behind the system.",
      title_3: "",
      body:
        "Ten years in luxury retail across Rome, Milan and London, made one pattern impossible to miss: people rarely buy because they lack clothes. They buy because they cannot see what they own. Ordo Rex Studio makes it visible.",
    },

    // ── Method ───────────────────────────────────────────────
    method: {
      eyebrow: "The Method",
      title_1: "A full wardrobe and",
      title_2: "nothing to wear",
      title_3: "is not a fashion problem.",
      subtitle: "It is a system problem, and Ordo Rex Studio was built to solve it.",
      aside_label: "Five Movements. One System.",
      cta_1: "Every relationship",
      cta_2: "begins with The Edit.",
    },
    manifesto: [
      {
        n: "01",
        title: "Observe",
        body:
          "Every piece comes out. Every drawer opened, every hanger checked, every folded stack unfolded. Sweaters piled on the bed, shoes lined up on the floor, coats on the rail. Before decisions, there is only seeing.",
      },
      {
        n: "02",
        title: "Edit",
        body:
          "What no longer belongs is removed with care. Not with violence. The pieces that carried you through a previous chapter are thanked, and released. What stays is what still speaks in your voice.",
      },
      {
        n: "03",
        title: "Structure",
        body:
          "The remaining wardrobe is arranged so you can see it. By category, by colour, by season, by function. Nothing hidden. Nothing forgotten. A system that survives Monday mornings.",
      },
      {
        n: "04",
        title: "Refine",
        body:
          "Outfit formulas are documented. Colour palette clarified. The three or four combinations that make you feel most yourself are named, so you can return to them without thinking.",
      },
      {
        n: "05",
        title: "Sustain",
        body:
          "A wardrobe is not built once. It is maintained. Seasonal refreshes, occasion sourcing, and the private line for the moments in between. The system stays alive because we tend to it.",
      },
    ],

    // ── Services ─────────────────────────────────────────────
    servicesPage: {
      eyebrow: "Services",
      title_1: "Eight ways",
      title_2: "to work together.",
      unsure: "Not sure where to begin? Neither are most clients.",
      what_we_do: "What we do",
      format: "Format",
      leave_with: "What you leave with",
      dialog_enquire: (name) => `Private enquiry for ${name}`,
      dialog_enquire_short: "Private enquiry",
      service_label: "Service",
    },
    services: [
      {
        id: "the-edit",
        number: "01",
        name: "The Edit",
        tagline: "A dedicated wardrobe curation. Because most people have no idea what they actually own.",
        format: "Half day / A day",
        image: "the-edit",
        what_we_do:
          "A single, focused session inside your wardrobe. Every piece comes out. What no longer belongs is removed with care. What remains is arranged so you can see it.",
        bullets: [
          "Piece-by-piece review of everything you own",
          "Decisions on keep, alter, sell, donate",
          "Basic rehanging by category and colour",
          "A clear picture of the wardrobe you actually own",
        ],
        what_you_leave_with: "Clarity. And a wardrobe you recognise.",
      },
      {
        id: "the-system",
        number: "02",
        name: "The System",
        tagline: "Everything in The Edit + colour analysis, body shape and outfit formulas built around you",
        format: "Two days",
        image: "the-system",
        what_we_do: "The complete foundation. We edit, then we build the operating system around who you are.",
        bullets: [
          "Full wardrobe edit",
          "Personal colour analysis",
          "Body shape and proportion study",
          "Signature outfit formulas documented",
        ],
        what_you_leave_with: "A wardrobe that thinks with you. Not against you.",
      },
      {
        id: "the-refresh",
        number: "03",
        name: "The Refresh",
        tagline: "The system you built does not maintain itself. Every season, we revisit, edit and update.",
        format: "Half day",
        image: "the-refresh",
        what_we_do:
          "A returning-client session. Seasonal transitions, new additions integrated, drift corrected before it becomes chaos.",
        bullets: [
          "Rotation between seasons",
          "Absorb any new purchases into the system",
          "Remove what has quietly stopped working",
          "Small refinements to formulas",
        ],
        what_you_leave_with: "A wardrobe that stays on course.",
      },
      {
        id: "the-private",
        number: "04",
        name: "The Private",
        tagline: "The person you call before you buy, before you pack, before you walk into the room.",
        format: "Ongoing · monthly",
        image: "the-private",
        what_we_do:
          "A quiet, always-on relationship. Text me from the boutique. Send the packing list before the flight. Ask me about the dress before you commit.",
        bullets: [
          "Direct, private line of contact",
          "Purchase second-opinions in real time",
          "Packing and travel edits",
          "Occasion planning as it arises",
        ],
        what_you_leave_with: "Never dressing alone again.",
      },
      {
        id: "the-new-chapter",
        number: "05",
        name: "The New Chapter Edit",
        tagline:
          "Life changes. Wardrobes rarely follow. This is the session for the space between who you were and who you are becoming.",
        format: "Half day",
        image: "the-new-chapter",
        what_we_do:
          "A promotion, a relocation, a divorce, a new relationship, a baby, an empty nest, a new city. Whatever shifted, the wardrobe needs to catch up.",
        bullets: [
          "A thorough curation with a focus on who you are becoming",
          "An honest conversation about the shift",
          "Identification of what belongs to the previous chapter",
          "Construction of a wardrobe for your new identity",
          "A targeted shopping plan to complete the transition",
        ],
        what_you_leave_with: "Everything in your wardrobe belongs to the person you are now.",
      },
      {
        id: "the-executive",
        number: "06",
        name: "The Executive Edit",
        tagline:
          "For founders and executives. A wardrobe built on autopilot, so that when they wake up, the hardest part of the day is already behind them.",
        format: "One day",
        image: "the-executive",
        what_we_do:
          "For people whose calendar is the enemy. We design the wardrobe so the morning decision disappears.",
        bullets: [
          "Uniform architecture: 3–5 pre-approved looks",
          "Travel & board-room protocols",
          "Investment-piece audit",
          "Colour and fit standardisation",
        ],
        what_you_leave_with: "One less decision. Every morning. Forever.",
      },
      {
        id: "the-occasion",
        number: "07",
        name: "The Occasion Edit",
        tagline:
          "A Carolyn Bessette-Kennedy dinner, a safari honeymoon, a ski chalet weekend, seven days in Shanghai for business.",
        format: "Bespoke",
        image: "the-occasion",
        what_we_do: "Whatever the occasion demands we source it, style it, and make it entirely yours.",
        bullets: [
          "Occasion brief and moodboard",
          "Sourcing from personal contacts and ateliers",
          "Full fitting and alteration coordination",
          "On-the-day styling if required",
        ],
        what_you_leave_with: "The version of you the room will remember.",
      },
      {
        id: "the-shopping",
        number: "08",
        name: "The Shopping Experience",
        tagline:
          "You shop with someone who has seen your wardrobe and knows exactly what is missing. Nothing bought on impulse. Nothing bought in doubt.",
        format: "Half day (3–4 hours) or full day (6–7 hours)",
        image: "the-shopping",
        what_we_do:
          "In-person shopping in Vienna, Milan, Paris — or virtual companionship online. Pre-vetted rails, private appointments, unhurried.",
        bullets: [
          "Pre-briefed boutique visits",
          "Private appointments where possible",
          "Real-time fit and pairing advice",
          "Only what completes the system",
        ],
        what_you_leave_with: "Only what belongs. Nothing more.",
      },
    ],

    // ── Studio ──────────────────────────────────────────────
    studio: {
      eyebrow: "The Studio",
      title_1: "The person",
      title_2: "behind the system.",
      eyebrow_2: "On method & obsession",
      lead: "It was never about having enough. It was about the missing thread between what someone owned and who they were becoming.",
      lead_italic: "Victoria von Rex, Ordo Rex Studio Founder",
      body: [
        "After more than a decade working within some of the world's most prestigious luxury houses across Rome, Milan and London, I founded Ordo Rex Studio from a pattern I had seen time and again: beautiful wardrobes, strong personal taste, and yet no real sense of ease.",
        "It was never about having enough. It was about the missing thread between what someone owned and who they were becoming.",
        "Ordo Rex Studio builds that thread, creating wardrobes that feel clearer, more coherent and more considered.",
        "Based in Vienna and working across Europe, I lead every engagement in the client's home, from the first hanger lifted to the final outfit photographed.",
      ],
      quote: "\u201COrdo — quiet order. Rex — the standard we hold ourselves to.\u201D",
      fig: "Fig. 002 — The Founder",
      principles_eyebrow: "Principles",
      principles: [
        { n: "I.", title: "In person.", body: "No remote consultations. Every session is conducted physically inside the wardrobe." },
        { n: "II.", title: "Discreet.", body: "Nothing is shared, photographed or discussed without your written consent." },
        { n: "III.", title: "Unaffiliated.", body: "No brand deals. No commissions. When we shop, I am on your side of the table." },
      ],
      ready: "Ready when you are.",
    },

    // ── FAQ ─────────────────────────────────────────────────
    faqPage: {
      eyebrow: "Frequently Asked",
      title_1: "Questions,",
      title_2: "answered privately.",
      aside_title: "Frequently asked",
      aside_body:
        "Answers to what most clients ask before we begin. If yours is not here, write to me directly.",
      still: "Still have a question?",
      write: "Write a private enquiry",
    },
    faqs: [
      {
        q: "Where do you work?",
        a: "Sessions take place in your home. Vienna is home base, and I travel across Europe by arrangement. There are no remote consultations — everything happens in person, inside the wardrobe itself.",
      },
      {
        q: "How do we begin?",
        a: "Every relationship begins with The Edit. It is where I learn what you own, and where you learn what you actually reach for. From there, we choose the next movement together.",
      },
      {
        q: "How long does a session take?",
        a: "The Edit is a half or full day. The System is two days. Refreshes are shorter. Occasion and shopping work is scoped to the brief. Time is generous by design — the work is not rushed.",
      },
      {
        q: "Do you sell clothes to me?",
        a: "No. I have no affiliation with any brand or boutique. When shopping is part of the work, I am on your side of the table, not the store's.",
      },
      {
        q: "What languages do you work in?",
        a: "Sessions in English, Italian and Spanish.",
      },
      {
        q: "What is your policy on discretion?",
        a: "Absolute. Every client is private. Nothing is shared, photographed or discussed without written consent.",
      },
    ],

    // ── Enquiry ────────────────────────────────────────────
    enquiry: {
      eyebrow: "Private Enquiry",
      title_1: "A private",
      title_2: "enquiry.",
      lead:
        "All new client relationships begin with a private enquiry. Every message is read and answered personally within 24 hours.",
      first_name: "First name",
      last_name: "Last name",
      email: "Email",
      phone: "Phone (optional)",
      location: "Where do you live",
      message: "Tell me briefly what brings you here",
      services_label: "Services of interest",
      not_sure: "Not sure yet",
      submit: "Submit private enquiry",
      sending: "Sending",
      disclaimer: "By submitting you agree to be contacted about your enquiry. Nothing shared, ever.",
      or_write: "Or write directly:",
      success_title_1: "Thank you.",
      success_title_2: "Your enquiry has been received.",
      success_body: "A confirmation is on its way to your inbox. I will reply personally within 24 hours.",
      send_another: "Send another →",
      error_select_service: "Please select at least one service.",
      success_toast: "Your enquiry has been sent. I will reply personally within 24 hours.",
      error_toast: "Something went wrong. Please try again.",
      prefill_message: (name) => `I would like to enquire about ${name}.`,
    },

    // ── Impressum ─────────────────────────────────────────
    impressum: {
      eyebrow: "Legal",
      title: "Impressum.",
      contact_label: "Contact",
      lines: ["Ordo Rex Studio", "Victoria von Rex", "Vienna, Austria"],
      note: "Private wardrobe strategy, by appointment only. Every written enquiry is read and answered personally.",
    },
  },

  // ══════════════════════════════════════════════════════════
  // DE — Deutsch
  // ══════════════════════════════════════════════════════════
  de: {
    nav: {
      home: "Start",
      method: "Methode",
      services: "Leistungen",
      studio: "Studio",
      faq: "FAQ",
      enquiry: "Anfrage",
      enquire: "Anfragen",
    },
    common: {
      begin_enquiry: "Private Anfrage starten",
      the_method: "Die Methode",
      view_all: "Alle acht Leistungen ansehen",
      read_full_method: "Die vollständige Methode lesen",
      meet_studio: "Die Gründerin kennenlernen",
      scroll: "Scrollen",
      volumeOf: (n, label) => `Band ${n} · ${label}`,
    },
    footer: {
      quiet_order_1: "Ordnung,",
      quiet_order_2: "gemacht um zu bleiben.",
      begin_link: "Private Anfrage starten →",
      studio: "Studio",
      studio_lines: ["Wien, Österreich", "Nach privater Terminvereinbarung"],
      studio_note: "Verfügbar in ganz Europa.\nKeine Fernberatungen.",
      studio_langs: "Sitzungen auf English · Italiano · Español",
      navigate: "Navigation",
      impressum: "Impressum",
      copyright: (y) => `© ${y} Ordo Rex Studio. Alle Rechte vorbehalten.`,
    },

    hero: {
      eyebrow_1: "Gegr. Wien",
      eyebrow_2: "Private Garderobenstrategie",
      studio_suffix: "STUDIO",
      description:
        "Private Garderobenstrategie für Menschen, die Klarheit, Struktur und einen verfeinerten persönlichen Stil suchen — persönlich durchgeführt, von derselben Hand, bei Ihnen zu Hause oder an Ihrer Seite.",
    },
    videoHero: {
      eyebrow: "Eine Geschichte stiller Ordnung",
      title: "ORDO REX STUDIO",
      subtitle: "Ein Erbe der Ordnung — privat gestaltet.",
      scroll: "Scrollen zum Eintreten",
      mute: "Ton",
      unmute: "Ton",
    },
    quote84: {
      eyebrow: "",
      pre: "Vierundachtzig Prozent der Menschen haben das Gefühl,",
      italic: "nichts anzuziehen zu haben.",
      post: "Ihr Kleiderschrank sieht das anders.",
      credit: "— Vestiaire Collective × WRÅD, 2024",
    },
    essence: {
      number: "I",
      eyebrow: "Das System",
      italic: "Eine Garderobe, die endlich Sinn ergibt.",
      heading: "Ordo Rex Studio ist das System hinter einer mühelosen Garderobe.",
      body:
        "Ob es darum geht, das Vorhandene zu editieren, die Richtung zu definieren, sich auf einen Anlass vorzubereiten oder einen Lebensübergang zu navigieren, jede Sitzung wird persönlich durchgeführt, von derselben Hand, bei Ihnen zu Hause, an Ihrer Seite.",
      meta_1: "Wien und in ganz Europa",
      meta_2: "Englisch · Italienisch · Spanisch",
      meta_3: "Keine Fernberatungen",
    },
    pillars: {
      eyebrow: "II — Die Methode",
      title_1: "Sie besitzen mehr Outfits, als Sie Kleidungsstücke besitzen.",
      title_2: "",
      items: [
        {
          key: "declutter",
          title: "Editieren",
          body: "Alles kommt heraus. Jedes Stück erhält eine Entscheidung: behalten, ändern lassen, verkaufen, spenden, archivieren, ersetzen. Was bleibt, wird katalogisiert — damit Sie genau wissen, was Sie besitzen.",
          link: "the-edit",
        },
        {
          key: "style",
          title: "Stil",
          body: "Outfits aus dem bauen, was Sie besitzen, Ihre Palette definieren und Ihren Signature-Look entdecken.",
          link: "the-system",
        },
        {
          key: "maintain",
          title: "Erhalten",
          body: "Saisonale Edits, Shopping-Begleitung und laufende Betreuung, damit Ihre Garderobe mühelos bleibt.",
          link: "the-refresh",
        },
      ],
      learn_more: "Mehr erfahren →",
    },
    instagram: {
      eyebrow: "Dem Studio folgen",
      title_1: "Ordnung, gemacht um zu bleiben",
      title_2: "",
      handle: "@ordorexstudio",
      cta: "Auf Instagram folgen →",
    },
    manifestoPreview: {
      eyebrow: "Manifest · Die Methode",
      title_1: "Ein voller Kleiderschrank",
      title_2: "und nichts anzuziehen",
      title_3: "ist kein Modeproblem.",
      subtitle: "Es ist ein Systemproblem.",
    },
    servicesTeaser: {
      eyebrow: "Acht Wege · Ein System",
      title_1: "Acht Wege,",
      title_2: "gemeinsam zu arbeiten.",
    },
    studioTeaser: {
      eyebrow: "III — Das Studio",
      title_1: "Die Person",
      title_2: "hinter dem System.",
      title_3: "",
      body:
        "Zehn Jahre im Luxusretail in Rom, Mailand und London. Lang genug, um zu erkennen: eine volle Garderobe und nichts anzuziehen ist keine Frage des Geschmacks — es ist eine Frage dessen, was man tatsächlich besitzt. In Wien wird daraus jetzt eine Methode.",
    },

    method: {
      eyebrow: "Die Methode",
      title_1: "Ein voller Kleiderschrank und",
      title_2: "nichts anzuziehen",
      title_3: "ist kein Modeproblem.",
      subtitle: "Es ist ein Systemproblem, und Ordo Rex Studio wurde geschaffen, um es zu lösen.",
      aside_label: "Fünf Bewegungen. Ein System.",
      cta_1: "Jede Beziehung",
      cta_2: "beginnt mit The Edit.",
    },
    manifesto: [
      {
        n: "01",
        title: "Beobachten",
        body:
          "Jedes Stück kommt heraus. Jede Schublade geöffnet, jeder Bügel geprüft, jeder gefaltete Stapel entfaltet. Pullover auf dem Bett gestapelt, Schuhe auf dem Boden aufgereiht, Mäntel auf der Stange. Vor Entscheidungen steht nur das Sehen.",
      },
      {
        n: "02",
        title: "Editieren",
        body:
          "Was nicht mehr dazugehört, wird mit Sorgfalt entfernt. Nicht mit Härte. Den Stücken, die Sie durch ein früheres Kapitel getragen haben, wird gedankt und sie werden losgelassen. Was bleibt, spricht weiterhin in Ihrer Stimme.",
      },
      {
        n: "03",
        title: "Strukturieren",
        body:
          "Die verbleibende Garderobe wird so angeordnet, dass Sie sie sehen können. Nach Kategorie, Farbe, Saison, Funktion. Nichts versteckt. Nichts vergessen. Ein System, das Montagmorgen übersteht.",
      },
      {
        n: "04",
        title: "Verfeinern",
        body:
          "Outfit-Formeln werden dokumentiert. Die Farbpalette geklärt. Die drei oder vier Kombinationen, in denen Sie sich am meisten wie Sie selbst fühlen, werden benannt — so können Sie zu ihnen zurückkehren, ohne nachzudenken.",
      },
      {
        n: "05",
        title: "Erhalten",
        body:
          "Eine Garderobe wird nicht einmal aufgebaut. Sie wird gepflegt. Saisonale Auffrischungen, Anlass-Sourcing und die private Linie für die Momente dazwischen. Das System bleibt lebendig, weil wir uns darum kümmern.",
      },
    ],

    servicesPage: {
      eyebrow: "Leistungen",
      title_1: "Acht Wege,",
      title_2: "gemeinsam zu arbeiten.",
      unsure: "Nicht sicher, wo Sie beginnen sollen? Die meisten Kundinnen auch nicht.",
      what_we_do: "Was wir tun",
      format: "Format",
      leave_with: "Was Sie mitnehmen",
      dialog_enquire: (name) => `Private Anfrage für ${name}`,
      dialog_enquire_short: "Private Anfrage",
      service_label: "Leistung",
    },
    services: [
      {
        id: "the-edit",
        number: "01",
        name: "The Edit",
        tagline: "Eine gezielte Garderoben-Kuration. Denn die meisten wissen nicht, was sie tatsächlich besitzen.",
        format: "Halber Tag / Ein Tag",
        image: "the-edit",
        what_we_do:
          "Eine einzelne, fokussierte Sitzung in Ihrem Kleiderschrank. Jedes Stück kommt heraus. Was nicht mehr dazugehört, wird mit Sorgfalt entfernt. Was bleibt, wird so angeordnet, dass Sie es sehen können.",
        bullets: [
          "Stück-für-Stück-Durchsicht von allem, was Sie besitzen",
          "Entscheidungen: behalten, ändern, verkaufen, spenden",
          "Grundlegende Neuordnung nach Kategorie und Farbe",
          "Ein klares Bild der Garderobe, die Sie tatsächlich besitzen",
        ],
        what_you_leave_with: "Klarheit. Und eine Garderobe, die Sie wiedererkennen.",
      },
      {
        id: "the-system",
        number: "02",
        name: "The System",
        tagline: "Alles aus The Edit + Farbanalyse, Körperform und maßgeschneiderte Outfit-Formeln",
        format: "Zwei Tage",
        image: "the-system",
        what_we_do: "Das vollständige Fundament. Wir editieren, dann bauen wir das Betriebssystem um Sie herum.",
        bullets: [
          "Vollständige Garderoben-Kuration",
          "Persönliche Farbanalyse",
          "Studie zu Körperform und Proportionen",
          "Signatur-Outfit-Formeln dokumentiert",
        ],
        what_you_leave_with: "Eine Garderobe, die mit Ihnen denkt. Nicht gegen Sie.",
      },
      {
        id: "the-refresh",
        number: "03",
        name: "The Refresh",
        tagline: "Das aufgebaute System pflegt sich nicht von selbst. Jede Saison überprüfen, editieren, aktualisieren wir.",
        format: "Halber Tag",
        image: "the-refresh",
        what_we_do:
          "Eine Sitzung für wiederkehrende Kundinnen. Saisonale Übergänge, neue Ergänzungen integriert, Abdrift korrigiert, bevor sie zum Chaos wird.",
        bullets: [
          "Rotation zwischen den Saisonen",
          "Neue Käufe ins System einfügen",
          "Entfernen, was still aufgehört hat zu funktionieren",
          "Kleine Verfeinerungen der Formeln",
        ],
        what_you_leave_with: "Eine Garderobe, die auf Kurs bleibt.",
      },
      {
        id: "the-private",
        number: "04",
        name: "The Private",
        tagline: "Die Person, die Sie anrufen, bevor Sie kaufen, packen oder den Raum betreten.",
        format: "Fortlaufend · monatlich",
        image: "the-private",
        what_we_do:
          "Eine stille, immer verfügbare Beziehung. Schreiben Sie mir aus der Boutique. Schicken Sie die Packliste vor dem Flug. Fragen Sie mich zum Kleid, bevor Sie sich entscheiden.",
        bullets: [
          "Direkter, privater Kontaktweg",
          "Zweitmeinungen zu Käufen in Echtzeit",
          "Packing- und Reise-Edits",
          "Anlassplanung, wann immer sie ansteht",
        ],
        what_you_leave_with: "Sie werden sich nie wieder allein anziehen.",
      },
      {
        id: "the-new-chapter",
        number: "05",
        name: "The New Chapter Edit",
        tagline:
          "Das Leben verändert sich. Garderoben folgen selten. Diese Sitzung ist für den Raum zwischen dem, wer Sie waren, und dem, wer Sie werden.",
        format: "Halber Tag",
        image: "the-new-chapter",
        what_we_do:
          "Eine Beförderung, ein Umzug, eine Scheidung, eine neue Beziehung, ein Baby, ein leeres Nest, eine neue Stadt. Was auch immer sich verändert hat, die Garderobe muss aufholen.",
        bullets: [
          "Gründliche Kuration mit Fokus darauf, wer Sie werden",
          "Ein ehrliches Gespräch über den Wandel",
          "Erkennung dessen, was zum vorherigen Kapitel gehört",
          "Aufbau einer Garderobe für Ihre neue Identität",
          "Ein gezielter Shopping-Plan zum Abschluss des Übergangs",
        ],
        what_you_leave_with: "Alles in Ihrer Garderobe gehört zu der Person, die Sie jetzt sind.",
      },
      {
        id: "the-executive",
        number: "06",
        name: "The Executive Edit",
        tagline:
          "Für Gründerinnen und Führungskräfte. Eine Garderobe im Autopilot — der schwierigste Teil des Tages liegt bereits hinter Ihnen, bevor Sie aufwachen.",
        format: "Ein Tag",
        image: "the-executive",
        what_we_do:
          "Für Menschen, deren Kalender der Feind ist. Wir gestalten die Garderobe so, dass die morgendliche Entscheidung verschwindet.",
        bullets: [
          "Uniform-Architektur: 3–5 freigegebene Looks",
          "Protokolle für Reise & Vorstandsräume",
          "Audit der Investment-Stücke",
          "Standardisierung von Farbe und Passform",
        ],
        what_you_leave_with: "Eine Entscheidung weniger. Jeden Morgen. Für immer.",
      },
      {
        id: "the-occasion",
        number: "07",
        name: "The Occasion Edit",
        tagline:
          "Ein Carolyn-Bessette-Kennedy-Dinner, eine Safari-Hochzeitsreise, ein Chalet-Wochenende, sieben Tage geschäftlich in Shanghai.",
        format: "Maßgeschneidert",
        image: "the-occasion",
        what_we_do: "Was der Anlass verlangt, beschaffen wir, stylen es und machen es ganz zu Ihrem.",
        bullets: [
          "Anlass-Briefing und Moodboard",
          "Sourcing über persönliche Kontakte und Ateliers",
          "Vollständige Koordination von Fitting und Änderungen",
          "Styling am Tag selbst, wenn gewünscht",
        ],
        what_you_leave_with: "Die Version von Ihnen, an die sich der Raum erinnern wird.",
      },
      {
        id: "the-shopping",
        number: "08",
        name: "The Shopping Experience",
        tagline:
          "Sie shoppen mit jemandem, der Ihre Garderobe gesehen hat und genau weiß, was fehlt. Nichts impulsiv gekauft. Nichts im Zweifel gekauft.",
        format: "Halber Tag (3–4 Std.) oder ganzer Tag (6–7 Std.)",
        image: "the-shopping",
        what_we_do:
          "Persönliches Shopping in Wien, Mailand, Paris — oder virtuelle Begleitung online. Vorab kuratierte Kollektionen, private Termine, in Ruhe.",
        bullets: [
          "Vorab gebriefte Boutique-Besuche",
          "Private Termine, wo möglich",
          "Echtzeit-Beratung zu Passform und Kombinationen",
          "Nur das, was das System vervollständigt",
        ],
        what_you_leave_with: "Nur das, was gehört. Nichts mehr.",
      },
    ],

    studio: {
      eyebrow: "Das Studio",
      title_1: "Die Person",
      title_2: "hinter dem System.",
      eyebrow_2: "Über Methode & Obsession",
      lead: "Es ging nie darum, genug zu haben. Es ging um den fehlenden Faden zwischen dem, was jemand besaß, und dem, wer er wurde.",
      lead_italic: "Victoria von Rex, Gründerin von Ordo Rex Studio",
      body: [
        "Nach mehr als einem Jahrzehnt in einigen der renommiertesten Luxushäuser in Rom, Mailand und London gründete ich Ordo Rex Studio aus einem Muster heraus, das ich immer wieder sah: schöne Garderoben, ausgeprägter persönlicher Geschmack — und dennoch keine wahre Leichtigkeit.",
        "Es ging nie darum, genug zu haben. Es ging um den fehlenden Faden zwischen dem, was jemand besaß, und dem, wer er wurde.",
        "Ordo Rex Studio baut diesen Faden — und schafft Garderoben, die klarer, kohärenter und durchdachter wirken.",
        "Von Wien aus in ganz Europa tätig, leite ich jede Zusammenarbeit persönlich beim Kunden zu Hause — vom ersten gehobenen Bügel bis zum letzten fotografierten Outfit.",
      ],
      quote: "\u201EOrdo — stille Ordnung. Rex — der Anspruch, den wir an uns selbst stellen.\u201C",
      fig: "Abb. 002 — Die Gründerin",
      principles_eyebrow: "Prinzipien",
      principles: [
        { n: "I.", title: "Persönlich.", body: "Keine Fernberatungen. Jede Sitzung findet physisch im Kleiderschrank statt." },
        { n: "II.", title: "Diskret.", body: "Nichts wird geteilt, fotografiert oder besprochen ohne Ihre schriftliche Zustimmung." },
        { n: "III.", title: "Unabhängig.", body: "Keine Markenverträge. Keine Provisionen. Beim Shoppen stehe ich auf Ihrer Seite des Tisches." },
      ],
      ready: "Bereit, wenn Sie es sind.",
    },

    faqPage: {
      eyebrow: "Häufige Fragen",
      title_1: "Fragen,",
      title_2: "privat beantwortet.",
      aside_title: "Häufig gestellt",
      aside_body:
        "Antworten auf das, was die meisten Kundinnen fragen, bevor wir beginnen. Ist Ihre Frage nicht dabei, schreiben Sie mir direkt.",
      still: "Noch eine Frage?",
      write: "Private Anfrage schreiben",
    },
    faqs: [
      {
        q: "Wo arbeiten Sie?",
        a: "Die Sitzungen finden bei Ihnen zu Hause statt. Wien ist die Heimatbasis, und ich reise nach Vereinbarung durch ganz Europa. Es gibt keine Fernberatungen — alles geschieht persönlich, im Kleiderschrank selbst.",
      },
      {
        q: "Wie beginnen wir?",
        a: "Jede Beziehung beginnt mit The Edit. Dort lerne ich, was Sie besitzen, und Sie lernen, wonach Sie tatsächlich greifen. Von dort aus wählen wir gemeinsam die nächste Bewegung.",
      },
      {
        q: "Wie lange dauert eine Sitzung?",
        a: "The Edit ist ein halber oder ganzer Tag. The System dauert zwei Tage. Refreshes sind kürzer. Anlass- und Shopping-Arbeit wird nach Briefing bemessen. Zeit ist bewusst großzügig — die Arbeit wird nicht überhastet.",
      },
      {
        q: "Verkaufen Sie mir Kleidung?",
        a: "Nein. Ich habe keine Verbindung zu Marken oder Boutiquen. Wenn Shopping Teil der Arbeit ist, sitze ich auf Ihrer Seite des Tisches, nicht auf der des Geschäfts.",
      },
      {
        q: "In welchen Sprachen arbeiten Sie?",
        a: "Sitzungen auf Englisch, Italienisch und Spanisch.",
      },
      {
        q: "Wie halten Sie es mit Diskretion?",
        a: "Absolut. Jede Kundin ist privat. Nichts wird geteilt, fotografiert oder besprochen ohne schriftliche Zustimmung.",
      },
    ],

    enquiry: {
      eyebrow: "Private Anfrage",
      title_1: "Eine private",
      title_2: "Anfrage.",
      lead:
        "Alle neuen Kundenbeziehungen beginnen mit einer privaten Anfrage. Jede Nachricht wird gelesen und persönlich innerhalb von 24 Stunden beantwortet.",
      first_name: "Vorname",
      last_name: "Nachname",
      email: "E-Mail",
      phone: "Telefon (optional)",
      location: "Wo Sie leben",
      message: "Erzählen Sie kurz, was Sie herführt",
      services_label: "Leistungen von Interesse",
      not_sure: "Noch unsicher",
      submit: "Private Anfrage senden",
      sending: "Wird gesendet",
      disclaimer: "Mit dem Absenden erklären Sie sich einverstanden, zu Ihrer Anfrage kontaktiert zu werden. Nichts wird jemals geteilt.",
      or_write: "Oder direkt schreiben:",
      success_title_1: "Vielen Dank.",
      success_title_2: "Ihre Anfrage ist eingegangen.",
      success_body: "Eine Bestätigung ist auf dem Weg in Ihr Postfach. Ich antworte persönlich innerhalb von 24 Stunden.",
      send_another: "Weitere senden →",
      error_select_service: "Bitte wählen Sie mindestens eine Leistung aus.",
      success_toast: "Ihre Anfrage wurde gesendet. Ich antworte persönlich innerhalb von 24 Stunden.",
      error_toast: "Etwas ist schiefgelaufen. Bitte erneut versuchen.",
      prefill_message: (name) => `Ich möchte eine Anfrage zu ${name} stellen.`,
    },

    // ── Impressum ─────────────────────────────────────────
    impressum: {
      eyebrow: "Rechtliches",
      title: "Impressum.",
      contact_label: "Kontakt",
      lines: ["Ordo Rex Studio", "Victoria von Rex", "Wien, Österreich"],
      note: "Private Garderobenstrategie, nur nach Vereinbarung. Jede schriftliche Anfrage wird persönlich gelesen und beantwortet.",
    },
  },
};
